// R-150: after wake/reveal/charm, retry each surviving flagged contest in target-row order.
// Only damage from the original caster or that caster's team qualifies; ties retain the status.
import { type JanusCtx, type Row, activeStatuses, ecId, ecLean, handBack, insertRoll, itemField, record, scalar, upper, definitionNotHeld } from '../context.js';
import { type JanusInput } from '../janus.js';
import { effectiveAbilityDc } from '../effective-ability-dc.js';
import { carrierHasTrigger } from './status-trigger-gate.js';
import { triggerFamilyPreDraw } from '../trigger-executor.js';
import { type SelectedSkill, selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { skillRollArr } from '../skill-roll.js';
import { contestDirectionalCover } from '../cover/directional-cover.js';
import { removeStatusFromRow, statusItemId, statusPaused } from '../../statuses/expiry.js';
import { connectionSurvivors, planStoredStatusRemoval, removeStoredStatus, storedStatusHasSpecialTeardown } from './status-effect-removal-helpers.js';
import { skipUnported, unportedSkipRow } from '../../unported.js';

export const RETRY_ON_DAMAGE = 'target_retries_contesting_roll_on_damage_taken';
const CONTESTING_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const SUMMON_TAG = 'B84EBE3F-B385-4CEF-9C69-648086C4CB7D';
const DOMINATED = 'D2E7BA2C-562D-D4AC-7F71-13E4AC540484';
const CHARMED = 'DD4C0995-C3D2-43A3-CFC7-226C3005274C';

const statusItem = statusItemId;
const present = (v: unknown): boolean => v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0' && v !== false;
const connectionOf = (s: Row): string => upper(scalar(s.status_connected_id));

/** encounter_row_team_uuid: the lean EC column, else the wrapped team layer. */
function teamOf(ctx: JanusCtx, row: Row): string {
  const raw = ecLean(row, 'team') ?? ctx.read.value(row, 'team');
  return upper(record(raw)?.items ? record(record(raw)?.items)?.item_id : scalar(raw));
}

function definitionOf(ctx: JanusCtx, status: Row): Row | null {
  return ctx.read.definition(statusItem(status)) ?? record(status.status_arr);
}

export function statusRetriesContestOnDamage(ctx: JanusCtx, status: Row): boolean {
  const def = definitionOf(ctx, status);
  return !!def && present(scalar(ctx.read.value(def, RETRY_ON_DAMAGE)));
}

/** A flagged connection owns its Charmed/Dominated retry, so the ordinary charm break never rolls it twice. */
export function damageRetryGovernsCharmRow(ctx: JanusCtx, target: Row, status: Row): boolean {
  if (![DOMINATED, CHARMED].includes(statusItem(status))) return false;
  const connection = connectionOf(status);
  return /^[0-9A-F-]{36}$/.test(connection)
    && activeStatuses(ctx, target).some(s => connectionOf(s) === connection && statusRetriesContestOnDamage(ctx, s));
}

export interface ContestRetryPlan { target: string; dealerId: string; statusIds: string[] }

/** PHP's status-only, unpaused, non-self, dealer/team-qualified rows, in their native held order. */
function retryRows(ctx: JanusCtx, dealer: Row, target: Row): Row[] {
  const dealerId = ecId(dealer), targetId = ecId(target);
  if (!dealerId || !targetId || dealerId === targetId) return [];
  const dealerTeam = teamOf(ctx, dealer);
  return activeStatuses(ctx, target).filter(status => {
    if (!statusRetriesContestOnDamage(ctx, status) || statusPaused(status)) return false;
    const def = definitionOf(ctx, status)!;
    if (upper(ctx.read.value(def, 'roll_type')) !== CONTESTING_ROLL) return false;
    const casterId = upper(scalar(status.status_caster_id) ?? scalar(status.caster));
    if (!casterId || casterId === targetId) return false;
    const caster = ctx.members.get(casterId);
    if (!caster) handBack('retry_contest_on_damage:status-caster-not-member');
    return casterId === dealerId || !!dealerTeam && teamOf(ctx, caster) === dealerTeam;
  });
}

/** Each due row uses the same exact removal proof as wake; a fixed DC does not require a caster skill. */
function retryRowPlan(ctx: JanusCtx, status: Row, target: Row) {
  if (!scalar(status.status_id) || status.tick_added || ctx.statusesAdded.includes(status as never)) handBack('retry_contest_on_damage:pending-row');
  const def = definitionOf(ctx, status)!;
  const statusItem = statusItemId(status), statusCaster = ctx.members.get(upper(scalar(status.status_caster_id) ?? scalar(status.caster)))!;
  planStoredStatusRemoval(ctx, target, status);
  const fixedDc = effectiveAbilityDc(ctx, statusCaster, def);
  const casterSkill = fixedDc === null ? selectSkillForContestingRoll(ctx, statusCaster, ctx.read.entries(def, 'skill_roll_contester')) : null;
  const targetSkill = selectSkillForContestingRoll(ctx, target, ctx.read.entries(def, 'skill_roll_contestee'));
  const cover = contestDirectionalCover(ctx, def, statusCaster, target, targetSkill.id);
  targetSkill.adv += cover.advStacks;
  return { statusItem, statusCaster, casterSkill, targetSkill, fixedDc, autoWin: cover.autoWin };
}

/** State-only preflight of every qualifying held row, before the damage dice. */
export function planContestRetry(ctx: JanusCtx, dealer: Row, target: Row): ContestRetryPlan | null {
  const rows = retryRows(ctx, dealer, target);
  if (!rows.length) return null;
  for (const status of rows) retryRowPlan(ctx, status, target);
  return { target: ecId(target), dealerId: ecId(dealer), statusIds: rows.map(status => upper(scalar(status.status_id))) };
}

/** Positive vitality damage on a survivor only. Wake and earlier retries may have removed or changed later rows. */
export function performContestRetry(ctx: JanusCtx, input: JanusInput, plan: ContestRetryPlan, target: Row): void {
  const dealer = ctx.members.get(plan.dealerId);
  if (!dealer) handBack('retry_contest_on_damage:dealer-not-member');
  const due = retryRows(ctx, dealer, target).map(status => upper(scalar(status.status_id)));
  for (const statusId of new Set(due)) {
    const status = retryRows(ctx, dealer, target).find(row => upper(scalar(row.status_id)) === statusId);
    if (!status) continue;
    const retry = retryRowPlan(ctx, status, target);
    const retryInput: JanusInput = { ...input, casterId: ecId(retry.statusCaster), abilityId: retry.statusItem, targetIds: [plan.target],
      useAbilityVariable1: null, useAbilityVariable2: null };
    let casterResult: number;
    if (retry.fixedDc !== null) {
      casterResult = retry.fixedDc;
      insertRoll(ctx, { roll_type_1: CONTESTING_ROLL, roll_type_2: 'ability_dc', roll_item_id: retry.statusItem,
        by: retry.statusCaster, roll_result: casterResult });
    } else {
      casterResult = skillRollArr(ctx, retryInput, retry.statusCaster, retry.casterSkill!, null, 0, { isTarget: false });
    }
    const targetResult = skillRollArr(ctx, retryInput, target, retry.targetSkill, retry.statusCaster, casterResult, { asTarget: true, isTarget: true });
    if (!retry.autoWin && casterResult >= targetResult) continue;
    removeStoredStatus(ctx, target, status, 'single');
    insertRoll(ctx, { roll_type_1: 'status_effect_removed_after_contesting_roll_passed', roll_type_2: '', roll_item_id: retry.statusItem,
      by: target, to: target, roll_result: 0 });
  }
}

// ── The ORDINARY Charmed / Dominated damage break (attempt_to_break_charm_effects_if_damage_taken, retired from PHP 2026-10-18 a7b779189) ──
// RULE (docs/janus-self-loop.md §Charmed / Dominated damage-break ordering): after vitality damage > 0 from ANY dealer (no dealer or team
// condition), the FIRST target row whose catalog item is Charmed or Dominated — a flagged connection above is R-150's, never rolled twice —
// re-runs the status's contest against its ORIGINAL caster. The contest reads the row's definition, or the held origin row's when
// status_origin_id names one (by status_id or item id); a missing contestee defaults to Willpower, a missing roll type to contesting_roll.
// The holder escapes only by strictly beating the caster (a tie holds): the row leaves through the native single-connection removal plus the
// `status_effect_removed_after_contesting_roll_passed` row, and once the Charmed row is gone every surviving row on its connection is
// cleared too (the source's Concentrating, sibling echoes), without loss triggers. Order: wake → reveal → this → R-150 → damage triggers.
// A case the page cannot hold (an absent caster, a zone / summon on the connection, a non-contest roll type, a special teardown) commits the
// damage and logs a named unported skip — the charm stays — as the weapon / raw lanes did for every charm before this port.
const WILLPOWER = '922C5882-54DE-42D0-A5C8-8118AA1C858E';
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

export type CharmBreakPlan = { kind: 'skip'; reason: string } | {
  kind: 'contest'; target: string; statusCaster: Row; charmId: string; usedId: string; usedItem: string; connection: string;
  casterSkill: SelectedSkill | null; targetSkill: SelectedSkill; fixedDc: number | null; autoWin: boolean;
};

/** PRE-DRAW: null = no Charmed / Dominated row this damage breaks. Every hand-back is a held-definition fetch, decided on state. */
export function planCharmBreak(ctx: JanusCtx, target: Row): CharmBreakPlan | null {
  const targetId = ecId(target);
  const statuses = activeStatuses(ctx, target);
  const status = statuses.find((s) => [CHARMED, DOMINATED].includes(statusItem(s)) && !damageRetryGovernsCharmRow(ctx, target, s));
  if (!status) return null;
  const casterId = upper(scalar(status.status_caster_id) ?? scalar(status.caster));
  if (!UUID.test(casterId)) return null; // PHP: no caster – cannot contest
  const skip = (why: string): CharmBreakPlan => ({ kind: 'skip', reason: `server-trigger:break-charm-on-damage:${why}` });
  const caster = ctx.members.get(casterId);
  if (!caster || casterId === targetId) return skip(caster ? 'self-caster' : 'caster-not-member');
  const origin = upper(scalar(status.status_origin_id));
  const used = (origin && statuses.find((s) => upper(scalar(s.status_id)) === origin || statusItem(s) === origin)) || status;
  const usedItem = statusItem(used);
  // the contest's own rows carry this item (skill_roll_arr reads it as the used ability): the catalog definition must be held
  const def = ctx.read.definition(usedItem);
  if (!def) definitionNotHeld('charm_break_on_damage:definition-not-held', usedItem);
  const rollType = upper(scalar(ctx.read.value(def!, 'roll_type')));
  if (rollType && rollType !== CONTESTING_ROLL) return skip('roll-type'); // a damage / DoT contest re-enters its own native branch
  const charmId = upper(scalar(status.status_id)), usedId = upper(scalar(used.status_id));
  if (!UUID.test(charmId) || !UUID.test(usedId) || ctx.statusesAdded.includes(status as never) || ctx.statusesAdded.includes(used as never))
    return skip('pending-row');
  if (storedStatusHasSpecialTeardown(ctx, used)) return skip('special-removal');
  const connection = connectionOf(status);
  if (UUID.test(connection)) {
    const zones = record(ctx.encounter.statuses)?.target;
    if (Array.isArray(zones) && zones.some((z) => upper(record(z)?.status_connected_id) === connection)) return skip('zone-on-connection');
    for (const { status: s } of connectionSurvivors(ctx, connection, '')) {
      if (statusItem(s) === SUMMON_TAG) return skip('summon-tag');
      if (s.tick_added || !UUID.test(upper(scalar(s.status_id)))) return skip('pending-row');
    }
  }
  // remove_status_effect_status_id_single_connection's loss tail, settled before any die (the wake / R-150 shape)
  if (carrierHasTrigger(ctx, target, 'triggers_when_caster_loses_status_effect', 'charm_break_on_damage:lose-trigger', usedItem))
    triggerFamilyPreDraw(ctx, target, { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: usedItem });
  const fixedDc = effectiveAbilityDc(ctx, caster, def!);
  const casterSkill = fixedDc === null ? selectSkillForContestingRoll(ctx, caster, ctx.read.entries(def!, 'skill_roll_contester')) : null;
  const contestee = ctx.read.entries(def!, 'skill_roll_contestee');
  const targetSkill = selectSkillForContestingRoll(ctx, target, contestee.length ? contestee : [WILLPOWER]);
  const cover = contestDirectionalCover(ctx, def!, caster, target, targetSkill.id);
  targetSkill.adv += cover.advStacks;
  return { kind: 'contest', target: targetId, statusCaster: caster, charmId, usedId, usedItem, connection, casterSkill, targetSkill, fixedDc,
    autoWin: cover.autoWin };
}

/** After the damage row, the death branch and the wake, when vitality damage > 0 landed on a holder that is still standing. */
export function performCharmBreak(ctx: JanusCtx, input: JanusInput, plan: CharmBreakPlan, target: Row): void {
  if (ctx.composedDeaths?.includes(ecId(target)) || Number(ecLean(target, 'v_current') ?? 0) <= 0) return;
  if (plan.kind === 'skip') {
    const reason = `${plan.reason} (${String(ecLean(target, 'encounter_creature_name') ?? itemField(target, 'item_name') ?? 'Creature')})`;
    if (!ctx.rows.some((row) => row.roll_type_1 === 'janus_unported_skip' && row.rolls_full === reason))
      ctx.rows.push({ ...(ctx.dryRun ? unportedSkipRow(reason) : skipUnported(reason, { target: ecId(target) })), roll_by: ecId(target), roll_to: ecId(target) });
    return;
  }
  // PHP picks its row after the wake: a row an earlier step of this damage already ended rolls nothing
  const held = (id: string): Row | undefined => activeStatuses(ctx, target).find((s) => upper(scalar(s.status_id)) === id);
  const used = held(plan.usedId);
  if (!held(plan.charmId) || !used) return;
  const retryInput: JanusInput = { ...input, casterId: ecId(plan.statusCaster), abilityId: plan.usedItem, targetIds: [plan.target],
    useAbilityVariable1: null, useAbilityVariable2: null };
  let casterResult: number;
  if (plan.fixedDc !== null) {
    casterResult = plan.fixedDc;
    insertRoll(ctx, { roll_type_1: CONTESTING_ROLL, roll_type_2: 'ability_dc', roll_item_id: plan.usedItem, by: plan.statusCaster, roll_result: casterResult });
  } else {
    casterResult = skillRollArr(ctx, retryInput, plan.statusCaster, plan.casterSkill!, null, 0, { isTarget: false });
  }
  const targetResult = skillRollArr(ctx, retryInput, target, plan.targetSkill, plan.statusCaster, casterResult, { asTarget: true, isTarget: true });
  if (!plan.autoWin && casterResult >= targetResult) return;
  removeStoredStatus(ctx, target, used, 'single');
  insertRoll(ctx, { roll_type_1: 'status_effect_removed_after_contesting_roll_passed', roll_type_2: '', roll_item_id: plan.usedItem,
    by: target, to: target, roll_result: 0 });
  // the spell ends with its charm: every row still on the connection goes (status_deleted, no loss triggers)
  if (held(plan.charmId) || !UUID.test(plan.connection)) return;
  for (const { member, holder, status } of connectionSurvivors(ctx, plan.connection, '')) {
    const id = upper(scalar(status.status_id));
    if (!member || member === ctx.encounter) {
      const reason = `server-trigger:break-charm-on-damage:connected-row-not-member:${holder}`;
      if (!ctx.rows.some((row) => row.roll_type_1 === 'janus_unported_skip' && row.rolls_full === reason))
        ctx.rows.push(ctx.dryRun ? unportedSkipRow(reason) : skipUnported(reason, { connection: plan.connection, target: holder, statusId: id }));
      continue;
    }
    ctx.statusesRemoved.push({ status_id: id, status_item_id: statusItem(status), target: holder });
    for (const row of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(row, id);
  }
}
